// src/pages/ResetCredentials.jsx
import { useRef, useState } from "react";
import { Box, Button, Input, Text, VStack } from "@chakra-ui/react";
import useResetCredentials from "../features/resetCredentials/useResetCredentials";

export default function ResetCredentials() {
  const [email, setEmail] = useState("");
  const inputRef = useRef(null);

  const { submit, loading, status, message, error, submittedEmail } =
    useResetCredentials();

  const onInput = (e) => {
    inputRef.current?.setCustomValidity("");
    setEmail(e.target.value);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const r = await submit(email);

    // 入力バリデーションエラー（未入力/形式NG）
    if (r?.validationMessage) {
      inputRef.current?.setCustomValidity(r.validationMessage);
      inputRef.current?.reportValidity();
      return;
    }

    // 200 成功のときだけ入力欄をクリア
    if (r?.ok) {
      setEmail("");
      // ついでにフォーカス戻すと連続操作がしやすい
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  // churned 判定
  const isChurned =
    status === 410 &&
    ((typeof message === "string" &&
      message.toLowerCase().includes("membership is not active")) ||
      (typeof error === "string" && error.toLowerCase() === "gone"));

  // 未加入判定（401 or 410だがchurnedではない）
  const isNotFound = status === 401 || (status === 410 && !isChurned);

  // 統一CTA（About に一本化）
  const UNIFIED_CTA_URL =
    "https://www.skool.com/cyber-range/about?utm_source=tools-app&utm_medium=error&utm_campaign=unified-cta";

  return (
    <Box p={4}>
      <form noValidate onSubmit={onSubmit}>
        <Text fontSize="lg" mb={4}>
          Reset your credentials here
        </Text>

        <VStack align="stretch" spacing={4}>
          <Input
            ref={inputRef}
            type="email"
            placeholder="Enter your email"
            value={email}
            onInput={onInput}
            isDisabled={loading}
          />

          <Button
            type="submit"
            isLoading={loading}
            bg="black"
            color="white"
            _hover={{ bg: "gray.700" }}
          >
            Submit
          </Button>

          {loading && (
            <Text fontSize="sm">This process may take a few minutes…</Text>
          )}
        </VStack>
      </form>

      {status !== null && (
        <Box mt={6} lineHeight="tall">
          {/* 200: 成功 */}
          {status === 200 && (
            <Text fontSize="md">
              Account access was successfully provisioned for{" "}
              <Text as="span" color="blue.500" fontWeight="semibold">
                '{submittedEmail}'
              </Text>
              . You will receive an email from{" "}
              <b>cyber-range@lognpacific.com</b> with your credentials.
            </Text>
          )}

          {/* 410: churned（退会済み） */}
          {isChurned && (
            <Box>
              <Text fontSize="md" color="red.600" mb={3} maxW="70ch">
                The account for{" "}
                <Text as="span" color="blue.500" fontWeight="semibold">
                  '{submittedEmail}'
                </Text>{" "}
                was previously a member of The Cyber Range, but it is no longer
                active. To rejoin, please click{" "}
                <a
                  href="https://www.skool.com/cyber-range/about"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#3182ce", textDecoration: "underline" }}
                >
                  here
                </a>
                .
              </Text>

              <Text fontSize="sm" color="gray.600">
                If you’ve already rejoined but still see this message, please
                contact{" "}
                <a
                  href="mailto:cyber@lognpacific.com"
                  style={{ color: "#3182ce", textDecoration: "underline" }}
                >
                  cyber@lognpacific.com
                </a>
                .
              </Text>
            </Box>
          )}

          {/* 401 / 410: 見つからない（統一表示 + マイクロコピー + About の1ボタンCTA） */}
          {isNotFound && (
            <Box>
              <Text fontSize="md" color="red.600" mb={3} maxW="70ch">
                The email '{submittedEmail}' could not be found. Please make sure
                you entered the email you used for Skool. If you're a member and
                still see this, email{" "}
                <a
                  href="mailto:cyber@lognpacific.com"
                  style={{ color: "#3182ce", textDecoration: "underline" }}
                >
                  cyber@lognpacific.com
                </a>
                .
              </Text>

              <Text fontSize="sm" color="gray.600" mt={1}>
                Not a member yet? Learn about access
              </Text>
              <Button
                as="a"
                href={UNIFIED_CTA_URL}
                target="_blank"
                rel="noopener noreferrer"
                colorScheme="blue"
                mt={2}
              >
                Learn more on Skool
              </Button>
            </Box>
          )}

          {/* 403: Affiliate ブロック */}
          {status === 403 && (
            <Text fontSize="md" color="red.600">
              We don’t provide Azure or Tenable access to affiliate users. If you
              need access for a specific reason, please contact{" "}
              <a
                href="mailto:cyber@lognpacific.com"
                style={{ color: "#3182ce", textDecoration: "underline" }}
              >
                cyber@lognpacific.com
              </a>
              .
            </Text>
          )}

          {/* 400: API 由来の不正（入力バリデーション外） */}
          {status === 400 && (
            <Box>
              <Text fontSize="md" color="red.600" fontWeight="bold">
                Bad request.
              </Text>
              {message && (
                <Box
                  as="pre"
                  p={3}
                  bg="gray.100"
                  borderRadius="md"
                  whiteSpace="pre-wrap"
                  fontSize="sm"
                >
                  {message}
                </Box>
              )}
            </Box>
          )}

          {/* 500+ サーバー系 */}
          {status >= 500 && (
            <Box>
              <Text fontSize="md" mb={2} color="red.600" fontWeight="bold">
                A server error has occurred.
              </Text>
              <Box
                as="pre"
                p={3}
                bg="gray.100"
                borderRadius="md"
                whiteSpace="pre-wrap"
                fontSize="sm"
              >
                {message ?? ""}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
